/**
 * backpacks — which backpack a player wears, and where it sits on the body.
 *
 * Pure (no DOM, no PIXI) so the match renderer, the locker preview and the
 * tests all resolve a backpack the same way. `equippedBackpack` is a catalog
 * id from config/cosmetics.json `backpacks`; null or an unknown id means the
 * `isDefault` backpack, and an entry with no sprite (the catalog's 'none') is
 * how a player wears no backpack at all.
 */

export const NO_BACKPACK_ID = 'none';

// Placement in body pixels (the body is 80×80, rotated so "up" is the facing
// direction). The pack sits behind the player, centred on the body's back
// edge, and is drawn under the body so only its lower half shows.
const DEFAULT_LAYOUT = Object.freeze({ width: 46, height: 37, offsetY: 40 });

/**
 * The catalog entry to draw for a backpack id, or null for no backpack.
 * @param {string|null|undefined} backpackId
 * @param {Object|null} cosmeticsConfig - config/cosmetics.json
 * @returns {Object|null}
 */
export function resolveBackpack(backpackId, cosmeticsConfig) {
  const entries = cosmeticsConfig?.backpacks || [];
  const entry = entries.find((e) => e.id === backpackId) || entries.find((e) => e.isDefault) || null;
  return entry?.sprite ? entry : null;
}

/**
 * Size and offset of a backpack sprite, in body pixels.
 * @param {Object} entry - a resolved backpack entry
 * @returns {{ width: number, height: number, offsetY: number }}
 */
export function backpackLayout(entry) {
  return {
    width: entry?.width ?? DEFAULT_LAYOUT.width,
    height: entry?.height ?? DEFAULT_LAYOUT.height,
    offsetY: entry?.offsetY ?? DEFAULT_LAYOUT.offsetY
  };
}
